import { h } from 'preact';
import { Link } from "preact-router/match";
import { iconCartV2, typeClinicalAnalysis, typeVaccine, typeVaccineCombo } from "../../assets";
import s from "./productCard.module.css";
import {useAppContext, useBehavioralContext} from '../../contexts';
import {useProductCard} from "./useProductCard";

const ProductCard = (props) => {
    // Destructured props
    let {
        product,
        handleClick,
        dataTestId = "generic-card",
        additionalRootClasses = "border-[#eee]",
        vendor = "DEFAULT"
    } = props;

    // Component logic data
    const componentProps = useProductCard({
        product
    });

    // Destructured component logic data
    const {
        isButtonDisabled,
        handleSoldOutClick
    } = componentProps;

    // Retrieves the method to access the sidebar cart component's properties
    const { handleCart } = useBehavioralContext();

    // Sets the background color of the service type badge
    const labelBadgeColor = product["productLabel"] ? (
        product["productLabel"].toLowerCase() === "novo serviço" ? "yellowBadge"
            : product["productLabel"].toLowerCase() === "menor preço" ? "greenBadge"
                : "redBadge"
    ) : "";

    // Formats the product href without the .html suffix
    const formattedHref = product.href ? product.href.split(".html")[0] : "";

    // Checks for the special price flag inside the product data object
    const hasSpecialPrice =
        product.specialPrice && product.specialPrice !== "" && product.specialPrice !== "0,00";

    // An enum for all the icon types
    const serviceTypeIconEnum = {
        clinical_analysis: typeClinicalAnalysis,
        vaccine_combo: typeVaccineCombo,
        vaccine: typeVaccine
    }

    const productPrice = vendor === "DEFAULT" ? product?.price : product?.tablePrice?.price;

    // Accessibility feature data
    const { highContrast } = useAppContext();

    // The variable name says it all
    const outOfStockAlert =
        product?.inStock ? null : (
            <div className={`bg-[#F0F2F7] w-full px-3 md:px-6 py-4`}>
                <p className={`text-[#9B51DF]`}>
                    Serviço fora de estoque no momento.
                </p>
            </div>
        );

    return (
        <div
            className={`${s.productCard} ${additionalRootClasses}`}
            data-testid={dataTestId}
        >
            <Link
                href={`/${formattedHref}`}
                onClick={() => handleClick(product)}
                className={`${s.firstSection}`}
            >
                {!!product.icon && (
                    <div className={s.cardIcon}>
                        <img
                            src={serviceTypeIconEnum[product.icon]}
                            alt={"Card icon"}
                            data-testid={`product-card-icon-${formattedHref}`}
                            className='dark:grayscale'
                        />
                    </div>
                )}
                {!!product.productLabel && (
                    <div
                        className={`${s.serviceType} ${s[labelBadgeColor]} dark:bg-contrast-yellow`}
                        data-testid={`product-card-badge-container-${formattedHref}`}
                    >
                        <p data-testid={`product-card-badge-${formattedHref}`} className={"dark:text-black"}>
                            {product.productLabel}
                        </p>
                    </div>
                )}
                <h3
                    className={`text-ellipsis overflow-hidden ${s.productName} ${highContrast ? "highTitleFirst" : ""}`}
                    data-testid={`product-card-name-${formattedHref}`}
                >
                    {product.name}
                </h3>
            </Link>
            <div className={`w-full`}>
                {outOfStockAlert}

                <div className={`${s.secondSection}`}>
                    <div className={`flex-col items-start gap-2 md:gap-1 mb-[0.5rem] ${product?.inStock ? "flex" : "hidden"}`}>
                        <div className={`${s.specialPriceWrapper} ${hasSpecialPrice || vendor !== "DEFAULT" ? '' : 'hidden'}`}>
                            <div
                                className={`${s.lineThrough} dark:border-b-contrast-yellow`}
                                data-testid={`product-line-${product.id}`}
                            />
                            <p
                                className={`${s.specialPrice} text-[#555] dark:text-white`}
                                data-testid={`product-special-price-${product.id}`}
                            >
                                {product.priceLabel} {product.price}
                            </p>
                        </div>
                        <div className={`flex flex-col md:flex-row items-end gap-[0.2rem]`}>
                            <h2
                                className={`font-bold leading-none ${s.productPrice} text-[#212b36] dark:text-white`}
                                data-testid={`product-card-price-${formattedHref}`}
                            >
                                {product.priceLabel} {hasSpecialPrice && vendor === "DEFAULT" ? product.specialPrice : productPrice}&nbsp;

                                {vendor !== "DEFAULT" ? (
                                    <span
                                        className={`leading-5 !font-normal ${s.installmentRules} dark:text-white`}
                                        data-testid={`product-card-installment-${formattedHref}`}
                                    >
                                    {product?.tablePrice?.installment?.label}
                                </span>
                                ) : product.installments && product.installments.length > 0 && !product?.installments[0]?.label.includes("vista") && (
                                    <span
                                        className={`leading-5 !font-normal ${s.installmentRules} dark:text-white`}
                                        data-testid={`product-card-installment-${formattedHref}`}
                                    >
                                        {product.installments[product.installments.length - 1].label}
                                    </span>
                                )}
                            </h2>
                        </div>
                    </div>
                    <div
                        className={`flex flex-col md:flex-row gap-y-2.5 gap-x-3 relative z-10 ${product?.inStock ? "mt-5 md:mt-7" : ""}`}>
                        <Link
                            href={`/${formattedHref}`}
                            className={`${s.infoButton} select-none ${highContrast ? "highFormButton" : ""}`}
                            data-testid={`product-card-button-${formattedHref}`}
                            onClick={() => handleClick(product)}
                        >
                            Saiba mais
                        </Link>
                        <button
                            type={"button"}
                            className={`${s.buyButton} select-none ${highContrast ? "highFormButton" : ""} ${product?.inStock ? "flex" : "hidden"}`}
                            data-testid={`product-card-button-${formattedHref}`}
                            onClick={() => handleCart("_", "_", {
                                isOpen: true,
                                newProductData: {},
                                url: `${formattedHref}`
                            })}
                        >
                            <img
                                src={iconCartV2}
                                alt=""
                            />
                            Comprar
                        </button>
                        <button
                            type={"button"}
                            className={`${s.alertButton} select-none ${highContrast ? "highFormButton" : ""} ${product?.inStock ? "hidden" : "flex"} ${isButtonDisabled ? "opacity-40" : ""}`}
                            data-testid={`product-card-button-${formattedHref}`}
                            onClick={() => handleSoldOutClick()}
                            disabled={isButtonDisabled}
                        >
                            Avise-me
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProductCard;
